import { useState } from "react";
import { TraceTimeline } from "./components/TraceTimeline";
import {
  SCENARIOS,
  runScenario,
  type ScenarioId,
  type ScenarioRun,
} from "./demo-scenarios";

export default function App() {
  const [selected, setSelected] = useState<ScenarioId>("safe_booking");
  const [run, setRun] = useState<ScenarioRun | null>(null);
  const [loading, setLoading] = useState(false);

  const execute = async () => {
    setLoading(true);
    try {
      setRun(await runScenario(selected));
    } finally {
      setLoading(false);
    }
  };

  const guardEvents =
    run?.result.trace.filter((event) => event.stage === "guard").length ?? 0;

  return (
    <main>
      <header className="hero">
        <div className="hero-copy">
          <p className="eyebrow">Synthetic reference implementation</p>
          <h1>
            Agent actions you can
            <span> inspect before trusting.</span>
          </h1>
          <p className="intro">
            A bounded tool loop proposes actions. A deterministic guard checks
            an action-scoped confirmation signal, freshness, availability, and
            idempotency before the mock CRM can mutate.
          </p>
        </div>
        <div className="integrity-badge">
          <span className="pulse" />
          External network disabled
        </div>
      </header>

      <section className="control-panel" aria-labelledby="scenario-title">
        <div>
          <p className="eyebrow">Choose a failure boundary</p>
          <h2 id="scenario-title">Run a synthetic scenario</h2>
        </div>
        <div className="scenario-grid">
          {SCENARIOS.map((scenario) => (
            <button
              className={selected === scenario.id ? "scenario active" : "scenario"}
              key={scenario.id}
              onClick={() => {
                setSelected(scenario.id);
                setRun(null);
              }}
              type="button"
            >
              <strong>{scenario.label}</strong>
              <span>{scenario.summary}</span>
            </button>
          ))}
        </div>
        <button
          className="run-button"
          disabled={loading}
          onClick={execute}
          type="button"
        >
          {loading ? "Running bounded loop…" : "Run scenario"}
        </button>
      </section>

      {run ? (
        <>
          <section className="result-grid" aria-label="Scenario outcome">
            <article className="metric-card">
              <span>Outcome</span>
              <strong>{run.result.outcome.kind}</strong>
            </article>
            <article className="metric-card">
              <span>Guard decisions</span>
              <strong>{guardEvents}</strong>
            </article>
            <article className="metric-card">
              <span>CRM mutations</span>
              <strong>{run.mutationCount}</strong>
            </article>
            <article className="metric-card">
              <span>Executions</span>
              <strong>{run.executionCount}</strong>
            </article>
          </section>

          <section className="inspection-grid">
            <article className="panel">
              <div className="panel-heading">
                <div>
                  <p className="eyebrow">Execution evidence</p>
                  <h2>Trace timeline</h2>
                </div>
                <span className="synthetic-chip">100% synthetic</span>
              </div>
              <TraceTimeline events={run.result.trace} />
            </article>

            <aside className="panel outcome-panel">
              <p className="eyebrow">Final response</p>
              <h2>Decision and outcome</h2>
              <p className="agent-reply">{run.result.reply}</p>
              <pre>{JSON.stringify(run.result.outcome, null, 2)}</pre>
              <div className="invariant">
                <strong>Invariant</strong>
                <p>
                  A planner may propose a mutation. Only the deterministic guard
                  and the adapter can authorize it; free-form message text cannot.
                </p>
              </div>
            </aside>
          </section>
        </>
      ) : (
        <section className="empty-state">
          <p>Select a scenario and run it to inspect every decision boundary.</p>
        </section>
      )}

      <footer>
        No customer data · no provider keys · no production claims · no hidden
        network calls
      </footer>
    </main>
  );
}
